import { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@paulownia/shared/components/ui/dialog';
import { Button } from '@paulownia/shared/components/ui/button';
import { onOrderSnapshot } from '@paulownia/shared/services/orders';
import { listProducts } from '@paulownia/shared/services/products';
import { initCustomerAuth } from '@/lib/customer-auth';
import { getOptionLabels, calculateOrderTotal, formatQuantityDisplay, safeToFixed } from '@paulownia/shared/types';
import type { Order, Product, OrderStatus } from '@paulownia/shared/types';
import {
  Clock,
  CheckCircle,
  Package,
  Loader2,
  Bell,
  Check,
  XCircle,
  ShoppingBag,
  Hourglass,
  Repeat,
} from 'lucide-react';

interface OrderDetailModalProps {
  orderId: string;
  tenantId: string;
  tenantName: string;
  pickupTime?: string;
  isOpen: boolean;
  onClose: () => void;
  onNavigateToMerchant: (slug: string) => void;
  tenantSlug: string;
}

interface StatusCardConfig {
  icon: React.ReactNode;
  title: string;
  description: string;
  bgColor: string;
  borderColor: string;
  iconBgColor: string;
  iconColor: string;
  titleColor: string;
  descriptionColor: string;
}

function getStatusCardConfig(status: OrderStatus, tenantName?: string, displayTime?: string): StatusCardConfig {
  switch (status) {
    case 'awaiting_validation':
      return {
        icon: <Hourglass className="w-5 h-5" />,
        title: 'En attente de confirmation',
        description: 'Le commerçant vérifie votre commande et s\'assure d\'avoir les produits disponibles.',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        iconBgColor: 'bg-amber-100',
        iconColor: 'text-amber-600',
        titleColor: 'text-amber-800',
        descriptionColor: 'text-amber-700',
      };
    case 'pending':
      return {
        icon: <Check className="w-5 h-5" />,
        title: 'Commande confirmée',
        description: displayTime ? `Retrait prévu à ${displayTime}` : 'En attente de retrait',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        iconBgColor: 'bg-blue-100',
        iconColor: 'text-blue-600',
        titleColor: 'text-blue-800',
        descriptionColor: 'text-blue-700',
      };
    case 'ready':
      return {
        icon: <Bell className="w-5 h-5" />,
        title: 'Votre commande est prête !',
        description: tenantName ? `Vous pouvez venir la retirer chez ${tenantName}` : 'Vous pouvez venir la retirer',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        iconBgColor: 'bg-green-100',
        iconColor: 'text-green-600',
        titleColor: 'text-green-800',
        descriptionColor: 'text-green-700',
      };
    case 'done':
      return {
        icon: <Package className="w-5 h-5" />,
        title: 'Commande terminée',
        description: 'Merci pour votre achat !',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
        iconBgColor: 'bg-gray-100',
        iconColor: 'text-gray-600',
        titleColor: 'text-gray-800',
        descriptionColor: 'text-gray-600',
      };
    default:
      return {
        icon: <Clock className="w-5 h-5" />,
        title: 'Statut inconnu',
        description: '',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
        iconBgColor: 'bg-gray-100',
        iconColor: 'text-gray-600',
        titleColor: 'text-gray-800',
        descriptionColor: 'text-gray-600',
      };
  }
}

const badgeConfig: Record<OrderStatus, {
  label: string;
  icon: typeof Clock;
  bgColor: string;
  textColor: string;
}> = {
  awaiting_validation: {
    label: 'En attente',
    icon: Hourglass,
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-800',
  },
  pending: {
    label: 'Confirmée',
    icon: CheckCircle,
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
  },
  ready: {
    label: 'Prête',
    icon: CheckCircle,
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
  },
  done: {
    label: 'Terminée',
    icon: Package,
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800',
  },
};

export function OrderDetailModal({
  orderId,
  tenantId,
  tenantName,
  pickupTime,
  isOpen,
  onClose,
  onNavigateToMerchant,
  tenantSlug,
}: OrderDetailModalProps) {
  const [order, setOrder] = useState<Order | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [wasDeleted, setWasDeleted] = useState(false);
  const [permissionError, setPermissionError] = useState(false);
  const hasReceivedOrderRef = useRef(false);

  useEffect(() => {
    if (!isOpen || !orderId) {
      return;
    }

    let unsubscribe: (() => void) | null = null;
    hasReceivedOrderRef.current = false;
    setLoading(true);
    setWasDeleted(false);
    setPermissionError(false);

    // Load products for this tenant
    listProducts(tenantId).then(setProducts).catch(console.error);

    // Subscribe to order updates
    initCustomerAuth()
      .then(() => {
        unsubscribe = onOrderSnapshot(
          orderId,
          (orderData) => {
            if (hasReceivedOrderRef.current && orderData === null) {
              setWasDeleted(true);
            }
            if (orderData !== null) {
              hasReceivedOrderRef.current = true;
            }
            setOrder(orderData);
            setLoading(false);
          },
          (error: { code?: string; message?: string }) => {
            console.error('[OrderDetailModal] Firestore error:', error.code, error.message);
            if (error.code === 'permission-denied') {
              setPermissionError(true);
            }
            setLoading(false);
          }
        );
      })
      .catch((error) => {
        console.error('[OrderDetailModal] Error initializing auth:', error);
        setLoading(false);
      });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [isOpen, orderId, tenantId]);

  const displayTime = pickupTime ? pickupTime.replace(':', 'h') : '';
  const badge = order ? (badgeConfig[order.status] || badgeConfig.pending) : badgeConfig.pending;
  const BadgeIcon = badge.icon;
  const statusCard = order ? getStatusCardConfig(order.status, tenantName, displayTime) : null;

  // Get product details for order lines
  const getProductName = (productId: string) => {
    const product = products.find(p => p.id === productId);
    return product?.name || 'Produit inconnu';
  };

  const getProductForLine = (productId: string) => {
    return products.find(p => p.id === productId);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-orange-600" />
            Ma commande chez {tenantName}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : permissionError ? (
          <div className="text-center py-6">
            <div className="mx-auto w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-3">
              <Clock className="w-6 h-6 text-orange-500" />
            </div>
            <p className="text-gray-600 text-sm mb-4">
              Le suivi en temps réel n'est pas disponible sur cet appareil.
            </p>
            <Button onClick={() => onNavigateToMerchant(tenantSlug)} className="w-full">
              Commander à nouveau
            </Button>
          </div>
        ) : !order ? (
          <div className="text-center py-6">
            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-3">
              <XCircle className="w-6 h-6 text-red-500" />
            </div>
            <p className="font-medium text-gray-900 mb-1">
              {wasDeleted ? 'Commande annulée' : 'Commande non trouvée'}
            </p>
            <p className="text-gray-600 text-sm mb-4">
              {wasDeleted
                ? 'Cette commande a été annulée par le commerçant.'
                : 'Impossible de trouver cette commande.'}
            </p>
            <Button onClick={onClose} variant="outline" className="w-full">
              Fermer
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Status Badge */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Client</p>
                <p className="font-medium">{order.customerName}</p>
              </div>
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${badge.bgColor}`}>
                <BadgeIcon className={`w-4 h-4 ${badge.textColor}`} />
                <span className={`text-sm font-medium ${badge.textColor}`}>
                  {badge.label}
                </span>
              </div>
            </div>

            {/* Status Card - Single unified status display */}
            {statusCard && (
              <div className={`${statusCard.bgColor} border ${statusCard.borderColor} rounded-xl p-4`}>
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full ${statusCard.iconBgColor} flex items-center justify-center flex-shrink-0`}>
                    <span className={statusCard.iconColor}>{statusCard.icon}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold ${statusCard.titleColor}`}>
                      {statusCard.title}
                    </p>
                    <p className={`text-sm mt-0.5 ${statusCard.descriptionColor}`}>
                      {statusCard.description}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Order Lines */}
            <div className="border-t pt-4">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Package className="w-4 h-4" />
                Détail de la commande
              </h3>
              <div className="space-y-2">
                {order.lines.map((line, index) => {
                  const product = getProductForLine(line.productId);
                  const optionLabels = product ? getOptionLabels(line, product) : [];

                  return (
                    <div
                      key={index}
                      className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {getProductName(line.productId)}
                        </p>
                        {optionLabels.length > 0 && (
                          <p className="text-sm text-gray-500">
                            {optionLabels.join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="font-medium text-gray-900">
                          {formatQuantityDisplay(line.quantity, product, line.portionSizeId, line)}
                        </span>
                        {line.preparedQuantity > 0 && line.preparedQuantity < line.quantity && (
                          <p className="text-xs text-orange-600">
                            {line.preparedQuantity}/{line.quantity} prêt
                          </p>
                        )}
                        {line.preparedQuantity >= line.quantity && (
                          <p className="text-xs text-green-600">Prêt</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Total */}
              {(() => {
                const total = calculateOrderTotal(order.lines, products);
                return total !== null && Number.isFinite(total) && (
                  <div className="flex items-center justify-between pt-3 mt-3 border-t border-gray-200">
                    <span className="font-medium text-gray-700">Total</span>
                    <span className="text-lg font-bold text-green-700">{safeToFixed(total, 2)} €</span>
                  </div>
                );
              })()}
            </div>

            {/* Note */}
            {order.note && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Note :</span> {order.note}
                </p>
              </div>
            )}

            {/* Recurring indicator */}
            {order.recurringOrderId && (
              <div className="flex items-center gap-2 text-sm text-orange-600 bg-orange-50 rounded-lg px-3 py-2">
                <Repeat className="h-4 w-4 flex-shrink-0" />
                <span>Commande récurrente — renouvelée chaque semaine</span>
              </div>
            )}

            {/* Order Reference */}
            <p className="text-xs text-gray-400 text-center">
              Ref: {orderId.slice(0, 8).toUpperCase()}
            </p>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={onClose}
              >
                Fermer
              </Button>
              <Button
                className="flex-1"
                onClick={() => onNavigateToMerchant(tenantSlug)}
              >
                Commander à nouveau
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
